import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { User, LoginRequest, RegisterRequest, AuthResponse } from '../models/user.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private readonly API_URL = environment.apiUrl;
  private readonly AUTH_ENDPOINTS = environment.authEndpoints;
  private readonly STORAGE_KEYS = environment.storage;

  private currentUserSubject = new BehaviorSubject<User | null>(this.getUserFromStorage());
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor() {
    // Check if token exists on service initialization
    const token = this.getToken();
    if (token && !this.isTokenExpired(token)) {
      this.currentUserSubject.next(this.getUserFromStorage());
    } else {
      this.logout();
    }
  }

  login(credentials: LoginRequest): Observable<AuthResponse> {
    // Use Keycloak login if enabled, otherwise use IVECO login
    const loginEndpoint = environment.keycloak.enabled
      ? this.AUTH_ENDPOINTS.keycloakLogin
      : this.AUTH_ENDPOINTS.login;

    return this.http.post<AuthResponse>(`${this.API_URL}${loginEndpoint}`, credentials).pipe(
      tap(response => this.handleAuthResponse(response))
    );
  }

  register(userData: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.API_URL}${this.AUTH_ENDPOINTS.register}`, userData).pipe(
      tap(response => this.handleAuthResponse(response))
    );
  }

  logout(): void {
    localStorage.removeItem(this.STORAGE_KEYS.tokenKey);
    localStorage.removeItem(this.STORAGE_KEYS.refreshTokenKey);
    localStorage.removeItem(this.STORAGE_KEYS.userKey);
    this.currentUserSubject.next(null);
  }

  refreshToken(): Observable<AuthResponse> {
    const refreshToken = this.getRefreshToken();
    return this.http.post<AuthResponse>(`${this.API_URL}${this.AUTH_ENDPOINTS.refresh}`, { refreshToken }).pipe(
      tap(response => this.handleAuthResponse(response))
    );
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.tokenKey);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEYS.refreshTokenKey);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  private handleAuthResponse(response: AuthResponse): void {
    localStorage.setItem(this.STORAGE_KEYS.tokenKey, response.token);
    if (response.refreshToken) {
      localStorage.setItem(this.STORAGE_KEYS.refreshTokenKey, response.refreshToken);
    }
    localStorage.setItem(this.STORAGE_KEYS.userKey, JSON.stringify(response.user));
    this.currentUserSubject.next(response.user);
  }

  private getUserFromStorage(): User | null {
    const userJson = localStorage.getItem(this.STORAGE_KEYS.userKey);
    return userJson ? JSON.parse(userJson) : null;
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp;
      return Date.now() >= expiry * 1000;
    } catch {
      return true;
    }
  }
}
